/**
 * Runtime frontend config (SON-124 Phase 6). One image is promoted across
 * environments, so docker/web/entrypoint.sh injects `window.__ENV` into
 * index.html at container start; `import.meta.env` stays as the fallback so
 * `vite dev`, vitest and the S3+CloudFront buildspec deploys are unchanged.
 * The namespaced `src/customer/` tree imports this copy by relative path.
 */

/** Allowlist. Must stay in sync with RUNTIME_ENV_KEYS in docker/web/entrypoint.sh. */
export const RUNTIME_ENV_KEYS = [
  "VITE_API_BASE_URL",
  "VITE_COGNITO_REGION",
  "VITE_COGNITO_USER_POOL_ID",
  "VITE_COGNITO_CLIENT_ID",
  "VITE_MEDIA_BASE_URL",
  "VITE_PUBLIC_ASSETS_BASE_URL",
  "VITE_AUTH_ENFORCE_WEB_APP_CONTEXT",
  "VITE_AUTH_MODE",
  "VITE_OIDC_ISSUER_URL",
  "VITE_OIDC_CLIENT_ID",
  "VITE_OIDC_REGISTER_URL",
  "VITE_OIDC_REALM",
  "VITE_AUTH_COOKIE_SESSION",
  "VITE_WEB_CUSTOMER_URL",
  "VITE_WEB_ADMIN_URL",
] as const;

export type RuntimeEnvKey = (typeof RUNTIME_ENV_KEYS)[number];

// Static `import.meta.env.<KEY>` member expressions: the only form Vite and
// vitest's `define` substitute. A dynamic `import.meta.env[key]` resolves to
// nothing in a production bundle.
const BUILD_TIME_ENV: Record<RuntimeEnvKey, string | undefined> = {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_COGNITO_REGION: import.meta.env.VITE_COGNITO_REGION,
  VITE_COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  VITE_COGNITO_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID,
  VITE_MEDIA_BASE_URL: import.meta.env.VITE_MEDIA_BASE_URL,
  VITE_PUBLIC_ASSETS_BASE_URL: import.meta.env.VITE_PUBLIC_ASSETS_BASE_URL,
  VITE_AUTH_ENFORCE_WEB_APP_CONTEXT: import.meta.env
    .VITE_AUTH_ENFORCE_WEB_APP_CONTEXT,
  VITE_AUTH_MODE: import.meta.env.VITE_AUTH_MODE,
  VITE_OIDC_ISSUER_URL: import.meta.env.VITE_OIDC_ISSUER_URL,
  VITE_OIDC_CLIENT_ID: import.meta.env.VITE_OIDC_CLIENT_ID,
  VITE_OIDC_REGISTER_URL: import.meta.env.VITE_OIDC_REGISTER_URL,
  VITE_OIDC_REALM: import.meta.env.VITE_OIDC_REALM,
  VITE_AUTH_COOKIE_SESSION: import.meta.env.VITE_AUTH_COOKIE_SESSION,
  VITE_WEB_CUSTOMER_URL: import.meta.env.VITE_WEB_CUSTOMER_URL,
  VITE_WEB_ADMIN_URL: import.meta.env.VITE_WEB_ADMIN_URL,
};

// Synchronous: several call sites read at module scope. An injected `null`
// (key the container was not given) falls through to the build-time value, so
// the existing per-call-site guards still decide hard-vs-soft failure.
export function runtimeEnv(key: RuntimeEnvKey): string | undefined {
  const injected =
    typeof window === "undefined" ? undefined : window.__ENV?.[key];
  if (typeof injected === "string" && injected !== "") return injected;
  return BUILD_TIME_ENV[key];
}
